import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button, Form, Badge, Alert, Nav } from 'react-bootstrap';
import { Line, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import Navbar from './Navbar';
import './PerformanceHistory.css';
import { API_ENDPOINTS } from './config/api';
import PageTitle from './components/PageTitle';

const MetricCard = ({ icon, iconClass, label, value, subtext }) => (
  <Card className="border-0 shadow-sm">
    <Card.Header className="p-2 px-3 border-0 d-flex justify-content-between align-items-center">
      <div className="text-uppercase smaller text-muted">{label}</div>
      <div className={`survey-results-metric-icon survey-results-metric-icon--${iconClass}`}>
        <i className={`bi ${icon}`} />
      </div>
    </Card.Header>
    <Card.Body>
      <h1 className="fw-bold mb-0">{value}</h1>
      <div className="text-muted small mb-0">{subtext}</div>
    </Card.Body>
  </Card>
);

const EmptyState = ({ message }) => (
  <div className="text-center py-5">
    <i className="bi bi-graph-up d-block mb-2" style={{ fontSize: 28, opacity: 0.4 }} />
    <small className="text-muted">{message}</small>
  </div>
);

const getAssessmentText = (completedCount, totalCount) => {
  if (totalCount === 0) return 'No evaluations assigned yet';
  const rate = (completedCount / totalCount) * 100;
  if (rate >= 100) return 'All evaluations completed — review trends below';
  if (rate >= 75) return 'Strong completion rate — trends available for completed evaluations';
  if (rate >= 50) return 'Moderate progress — more data will improve trend accuracy';
  return 'Limited data available — complete more evaluations to see trends';
};

const formatEvalDate = (date) => {
  if (!date) return '—';
  return new Date(date).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const getEvaluationTimelineDate = (evaluation) => {
  if (!evaluation) return null;
  if (evaluation.hasResponse) {
    return evaluation.response?.createdAt || evaluation.completedAt || evaluation.createdAt || evaluation.dueDate || null;
  }
  return evaluation.dueDate || evaluation.createdAt || null;
};

const getEvaluationTimelineLabel = (evaluation) => formatEvalDate(getEvaluationTimelineDate(evaluation));

const dedupeQuestions = (questions = []) => {
  const seen = new Set();
  return questions.filter((question, index) => {
    const keyParts = [
      (question?.questionType || 'unknown').trim(),
      (question?.questionText || '').trim(),
      JSON.stringify(question?.options || []),
      JSON.stringify(question?.columns || []),
      (question?.infoHtml || '').trim(),
      (question?.infoPlacement || '').trim(),
      question?.showInfo ? 'showInfo:1' : 'showInfo:0'
    ];
    const key = keyParts.join('::');

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const getEvaluatorName = (evaluator) => {
  if (!evaluator) return '—';
  const name = `${evaluator.firstName || ''} ${evaluator.lastName || ''}`.trim();
  return name || '—';
};

const DetailTile = ({ icon, iconBg, iconColor, label, value }) => (
  <div className="d-flex align-items-start gap-2 p-3 rounded-3 bg-light h-100">
    <div
      className={`d-inline-flex align-items-center justify-content-center rounded-3 ${iconBg}`}
      style={{ width: 40, height: 40 }}
    >
      <i className={`bi ${icon} ${iconColor}`} />
    </div>
    <div className="text-start">
      <div className="text-muted text-uppercase small fw-semibold">{label}</div>
      <div className="fw-semibold">{value}</div>
    </div>
  </div>
);

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Shared chart color palette used for both line and bar charts
const CHART_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f97316',
  '#eab308', '#84cc16', '#22c55e', '#06b6d4', '#3b82f6'
];

const PerformanceHistory = () => {
  const { residentId, templateId } = useParams();
  const navigate = useNavigate();
  const [performanceData, setPerformanceData] = useState(null);
  const [selectedYear, setSelectedYear] = useState(() => {
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();
    // Academic year starts in July, so if we're in July or later, use current year
    // If we're before July, use previous year
    return currentMonth >= 6 ? currentYear : currentYear - 1;
  });
  const [chartType, setChartType] = useState('line');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchPerformanceData();
  }, [residentId, templateId, selectedYear]);

  const fetchPerformanceData = async () => {
    try {
      const response = await fetch(
        `${API_ENDPOINTS.DIRECTOR}/performance-evaluations/${residentId}?year=${selectedYear}`,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        const templateData = data.find(group => group.templateId === parseInt(templateId));
        setPerformanceData(templateData);
      }
    } catch (error) {
      console.error('Error fetching performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSortedEvaluations = () => {
    if (!performanceData?.evaluations) return [];
    return [...performanceData.evaluations].sort(
      (a, b) => {
        const aDate = getEvaluationTimelineDate(a);
        const bDate = getEvaluationTimelineDate(b);

        if (!aDate && !bDate) return 0;
        if (!aDate) return 1;
        if (!bDate) return -1;

        return new Date(aDate) - new Date(bDate);
      }
    );
  };

  const isEvalComplete = (evaluation) => evaluation.hasResponse;

  const prepareChartData = (question) => {
    if (!performanceData || !performanceData.evaluations) return null;

    const sortedEvaluations = getSortedEvaluations();

    if (sortedEvaluations.length === 0) {
      return {
        labels: [],
        datasets: [{
          label: 'No evaluations assigned',
          data: [],
          borderColor: 'rgba(108, 117, 125, 0.6)',
          backgroundColor: 'rgba(108, 117, 125, 0.1)',
          tension: 0.4,
          fill: true
        }]
      };
    }

    const evalDates = sortedEvaluations.map(getEvaluationTimelineLabel);

    // Modern color palette (shared)
    const lineColors = CHART_COLORS;

    // Handle different question types
    switch (question.questionType) {
      case 'radio':
      case 'dropdown':
        // For radio and dropdown: show selected answer over time
        const radioData = sortedEvaluations.map(evaluation => {
          if (!isEvalComplete(evaluation)) return 0;

          const response = evaluation.response;
          const answer = response?.answers?.[question.id];

          if (answer && question.options && Array.isArray(question.options)) {
            const answerIndex = question.options.indexOf(answer);
            return answerIndex >= 0 ? answerIndex + 1 : 0;
          }
          return 0;
        });
        
        return {
          labels: evalDates,
          datasets: [
            {
              label: 'Selected Answer',
              data: radioData,
              borderColor: '#6366f1',
              backgroundColor: 'rgba(99, 102, 241, 0.1)',
              tension: 0.4,
              fill: true,
              borderWidth: 3,
              pointRadius: 6,
              pointHoverRadius: 8,
              pointBackgroundColor: '#6366f1',
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
            }
          ]
        };

      case 'checkbox':
        // For checkbox: plot question options on y-axis, time on x-axis
        if (question.options && Array.isArray(question.options)) {
          const optionData = question.options.map((option, optionIdx) => {
            const data = sortedEvaluations.map(evaluation => {
              if (!isEvalComplete(evaluation)) return 0;

              const response = evaluation.response;
              const answer = response?.answers?.[question.id];

              return Array.isArray(answer) && answer.includes(option) ? 1 : 0;
            });
            
            return {
              label: option,
              data: data,
              fill: false,
              borderColor: lineColors[optionIdx % lineColors.length],
              backgroundColor: lineColors[optionIdx % lineColors.length],
              tension: 0.4,
              spanGaps: true,
              borderWidth: 3,
              pointRadius: 6,
              pointHoverRadius: 8,
              pointBackgroundColor: lineColors[optionIdx % lineColors.length],
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
            };
          });
          
          return {
            labels: evalDates,
            datasets: optionData,
          };
        }
        break;

      case 'rating':
        // For rating scale: rating on y-axis, time on x-axis
        const ratingData = sortedEvaluations.map(evaluation => {
          if (!isEvalComplete(evaluation)) return 0;
          return evaluation.response?.answers?.[question.id] || 0;
        });
        
        return {
          labels: evalDates,
          datasets: [
            {
              label: 'Rating Score',
              data: ratingData,
              borderColor: '#6366f1',
              backgroundColor: 'rgba(99, 102, 241, 0.1)',
              tension: 0.4,
              fill: true,
              borderWidth: 3,
              pointRadius: 6,
              pointHoverRadius: 8,
              pointBackgroundColor: '#6366f1',
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
            }
          ]
        };

      case 'radio-matrix':
        // For performance matrix: column headers on y-axis, plot points for each factor over time
        if (question.columns && Array.isArray(question.columns)) {
          const factorData = question.options.map((factor, factorIdx) => {
            const data = sortedEvaluations.map(evaluation => {
              if (!isEvalComplete(evaluation)) return 0;

              const response = evaluation.response;
              const answer = response?.answers?.[question.id];

              if (answer && typeof answer === 'object' && answer[factorIdx.toString()] !== undefined) {
                return answer[factorIdx.toString()] + 1;
              }
              return 0;
            });
            
            return {
              label: factor,
              data: data,
              fill: false,
              borderColor: lineColors[factorIdx % lineColors.length],
              backgroundColor: lineColors[factorIdx % lineColors.length],
              tension: 0.4,
              spanGaps: true,
              borderWidth: 3,
              pointRadius: 6,
              pointHoverRadius: 8,
              pointBackgroundColor: lineColors[factorIdx % lineColors.length],
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
            };
          });
          
          return {
            labels: evalDates,
            datasets: factorData,
          };
        }
        break;

      default:
        // Default: single value per question (for text, textarea, etc.)
        const data = sortedEvaluations.map(evaluation => {
          if (!isEvalComplete(evaluation)) return 0;
          return evaluation.response?.answers?.[question.id] || 0;
        });

        let runningSum = 0;
        let runningCount = 0;
        const cumulativeData = sortedEvaluations.map((evaluation) => {
          if (!isEvalComplete(evaluation)) {
            return runningCount > 0 ? runningSum / runningCount : null;
          }
          const val = evaluation.response?.answers?.[question.id];
          if (val == null) {
            return runningCount > 0 ? runningSum / runningCount : null;
          }
          runningSum += val;
          runningCount += 1;
          return runningSum / runningCount;
        });
        
        return {
          labels: evalDates,
          datasets: [
            {
              label: 'Individual Scores',
              data: data,
              borderColor: '#6366f1',
              backgroundColor: 'rgba(99, 102, 241, 0.1)',
              tension: 0.4,
              fill: true,
              borderWidth: 3,
              pointRadius: 6,
              pointHoverRadius: 8,
              pointBackgroundColor: '#6366f1',
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
            },
            {
              label: 'Cumulative Average',
              data: cumulativeData,
              borderColor: '#ec4899',
              backgroundColor: 'rgba(236, 72, 153, 0.1)',
              tension: 0.4,
              borderDash: [8, 4],
              borderWidth: 3,
              pointRadius: 6,
              pointHoverRadius: 8,
              pointBackgroundColor: '#ec4899',
              pointBorderColor: '#ffffff',
              pointBorderWidth: 2,
            }
          ]
        };
    }
  };

  const prepareBarChartData = (question) => {
    if (!performanceData || !performanceData.evaluations) return null;

    const sortedEvaluations = getSortedEvaluations();

    if (sortedEvaluations.length === 0) {
      return {
        labels: [],
        datasets: [{
          label: 'No evaluations assigned',
          data: [],
          backgroundColor: 'rgba(108, 117, 125, 0.5)',
          borderColor: 'rgba(108, 117, 125, 0.8)',
          borderWidth: 1
        }]
      };
    }

    const evalDates = sortedEvaluations.map(getEvaluationTimelineLabel);

    // Handle different question types
    switch (question.questionType) {
      case 'radio':
      case 'dropdown':
        const radioBarData = sortedEvaluations.map(evaluation => {
          if (!isEvalComplete(evaluation)) return 0;

          const answer = evaluation.response?.answers?.[question.id];

          if (answer && question.options && Array.isArray(question.options)) {
            const answerIndex = question.options.indexOf(answer);
            return answerIndex >= 0 ? answerIndex + 1 : 0;
          }
          return 0;
        });

        return {
          labels: evalDates,
          datasets: [{
            label: 'Selected Answer',
            data: radioBarData,
            backgroundColor: CHART_COLORS[0],
            borderColor: CHART_COLORS[0],
            borderWidth: 2,
            borderRadius: 8,
            borderSkipped: false,
          }]
        };

      case 'checkbox':
        if (question.options && Array.isArray(question.options)) {
          const datasets = question.options.map((option, optionIdx) => {
            const data = sortedEvaluations.map(evaluation => {
              if (!isEvalComplete(evaluation)) return 0;

              const answer = evaluation.response?.answers?.[question.id];
              return Array.isArray(answer) && answer.includes(option) ? 1 : 0;
            });

            return {
              label: option,
              data: data,
              backgroundColor: CHART_COLORS[optionIdx % CHART_COLORS.length],
              borderColor: CHART_COLORS[optionIdx % CHART_COLORS.length],
              borderWidth: 2,
              borderRadius: 8,
              borderSkipped: false,
            };
          });

          return {
            labels: evalDates,
            datasets: datasets
          };
        }
        break;

      case 'rating':
        const ratingBarData = sortedEvaluations.map(evaluation => {
          if (!isEvalComplete(evaluation)) return 0;
          return evaluation.response?.answers?.[question.id] || 0;
        });

        return {
          labels: evalDates,
          datasets: [{
            label: 'Rating Score',
            data: ratingBarData,
            backgroundColor: CHART_COLORS[0],
            borderColor: CHART_COLORS[0],
            borderWidth: 2,
            borderRadius: 8,
            borderSkipped: false,
          }]
        };

      case 'radio-matrix':
        if (question.columns && Array.isArray(question.columns)) {
          const datasets = question.options.map((factor, factorIdx) => {
            const data = sortedEvaluations.map(evaluation => {
              if (!isEvalComplete(evaluation)) return 0;

              const answer = evaluation.response?.answers?.[question.id];

              if (answer && typeof answer === 'object' && answer[factorIdx.toString()] !== undefined) {
                return answer[factorIdx.toString()] + 1;
              }
              return 0;
            });

            return {
              label: factor,
              data: data,
              backgroundColor: CHART_COLORS[factorIdx % CHART_COLORS.length],
              borderColor: CHART_COLORS[factorIdx % CHART_COLORS.length],
              borderWidth: 2,
              borderRadius: 8,
              borderSkipped: false,
            };
          });

          return {
            labels: evalDates,
            datasets: datasets
          };
        }
        break;

      default:
        const data = sortedEvaluations.map(evaluation => {
          if (!isEvalComplete(evaluation)) return 0;
          return evaluation.response?.answers?.[question.id] || 0;
        });

        return {
          labels: evalDates,
          datasets: [{
            label: question.questionText,
            data: data,
            backgroundColor: CHART_COLORS[0],
            borderColor: CHART_COLORS[0],
            borderWidth: 2,
            borderRadius: 8,
            borderSkipped: false,
          }]
        };
    }
  };

  const renderTextResponse = (question) => {
    const responses = performanceData.evaluations
      .filter(evaluation => evaluation.hasResponse)
      .map(evaluation => {
        const response = evaluation.response;
        const answer = response?.answers?.[question.id];
        return answer !== undefined ? { evaluation, answer } : null;
      })
      .filter(Boolean);

    if (responses.length === 0) {
      return <EmptyState message="No responses available for this question." />;
    }

    return (
      <div>
        <div className="text-muted text-uppercase smaller mb-2">
          Responses <Badge className="bg-secondary small ms-1 p-1 px-2">{responses.length}</Badge>
        </div>
        {responses.map(({ evaluation, answer }, index) => (
          <div key={index} className="p-3 card-bg-container rounded bg-light small mb-2">
            <div className="d-flex justify-content-between align-items-start mb-2">
              <small className="text-muted">
                <i className="bi bi-calendar me-1" />
                {evaluation.completedAt ? new Date(evaluation.completedAt).toLocaleDateString() :
                  evaluation.createdAt ? new Date(evaluation.createdAt).toLocaleDateString() :
                  'Date not available'}
              </small>
              <Badge className="bg-success small">Completed</Badge>
            </div>
            <div>
              <i className="bi bi-quote me-2 text-muted" />
              {answer || <span className="text-muted">No response provided</span>}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderHeaderParagraph = (question) => {
    // Handle JSON structure for header-paragraph content
    let headerText = '';
    let paragraphText = '';
    let headerSize = 'h4';

    // Check if questionText is a JSON object
    if (question.questionText && typeof question.questionText === 'string') {
      try {
        const parsed = JSON.parse(question.questionText);
        if (parsed.header) headerText = parsed.header;
        if (parsed.paragraph) paragraphText = parsed.paragraph;
        if (parsed.size) headerSize = parsed.size;
      } catch (e) {
        // If not JSON, use as regular text
        headerText = question.questionText;
      }
    } else if (question.questionText) {
      headerText = question.questionText;
    }

    // Also check options for additional content
    if (question.options && question.options.length > 0) {
      if (!paragraphText) {
        // If no paragraph from questionText, use first option
        paragraphText = question.options[0];
      }
    }



    return (
      <div>
        {headerText && (
          <h5 className={`mb-2 ${headerSize === 'h1' ? 'h4' : headerSize === 'h3' ? 'h6' : 'h5'}`}>
            {headerText}
          </h5>
        )}
        {paragraphText && (
          <p className="survey-results-description-text mb-0" style={{ whiteSpace: 'pre-wrap' }}>
            {paragraphText}
          </p>
        )}
      </div>
    );
  };

  const getChartOptions = (chartType, question) => {
    // Determine legend position based on question type and number of factors
    let legendPosition = 'top';
    if (question.questionType === 'radio-matrix' && question.options && question.options.length > 4) {
      legendPosition = 'right';
    }

    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: legendPosition,
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
              weight: '500'
            }
          }
        },
        tooltip: {
          mode: 'index',
          intersect: false,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          titleColor: '#ffffff',
          bodyColor: '#ffffff',
          borderColor: 'rgba(255, 255, 255, 0.2)',
          borderWidth: 1,
          cornerRadius: 8,
          padding: 12,
          displayColors: true,
          callbacks: {
            label: function(context) {
              const label = context.dataset.label || '';
              const value = context.parsed.y;
              
              // Custom tooltip labels for different question types
              if (question.questionType === 'radio' || question.questionType === 'dropdown') {
                if (value === 0) return `${label}: Not yet completed`;
                if (question.options && Array.isArray(question.options)) {
                  return `${label}: ${question.options[value - 1] || 'Not Selected'}`;
                } else {
                  return `${label}: ${value === 1 ? 'Selected' : 'Not Selected'}`;
                }
              } else if (question.questionType === 'checkbox') {
                return `${label}: ${value === 1 ? 'Selected' : 'Not Selected'}`;
              } else if (question.questionType === 'radio-matrix') {
                const columns = question.columns || ['Unsatisfactory', 'Needs Improvement', 'Meets Expectations', 'Exceeds Expectations'];
                if (value === 0) return `${label}: Not yet completed`;
                return `${label}: ${columns[value - 1] || 'Not Rated'}`;
              } else if (question.questionType === 'rating') {
                if (value === 0) return `${label}: Not yet completed`;
                return `${label}: ${value}/5`;
              }

              if (value === 0) return `${label}: Not yet completed`;
              return `${label}: ${value}`;
            }
          }
        }
      },
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      }
    };

    if (chartType === 'line') {
      const yAxisConfig = {
        beginAtZero: true,
        title: {
          display: true,
          font: {
            size: 14,
            weight: '600'
          },
          color: '#374151'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        },
        ticks: {
          font: {
            size: 12
          },
          color: '#6b7280'
        }
      };

      // Configure y-axis based on question type
      if (question.questionType === 'radio' || question.questionType === 'dropdown') {
        // For radio and dropdown, show answer choices on y-axis
        if (question.options && Array.isArray(question.options)) {
          yAxisConfig.max = question.options.length;
          yAxisConfig.stepSize = 1;
          yAxisConfig.title.text = 'Answer Choice';
          yAxisConfig.ticks.callback = function(value) {
            if (value === 0) return 'Not Yet Completed';
            if (value >= 1 && value <= question.options.length) {
              return question.options[value - 1];
            }
            return '';
          };
        } else {
          yAxisConfig.max = 1;
          yAxisConfig.stepSize = 1;
          yAxisConfig.title.text = 'Selection Status';
          yAxisConfig.ticks.callback = function(value) {
            return value === 1 ? 'Selected' : 'Not Selected';
          };
        }
      } else if (question.questionType === 'checkbox') {
        yAxisConfig.max = 1;
        yAxisConfig.stepSize = 1;
        yAxisConfig.title.text = 'Selection Status';
        yAxisConfig.ticks.callback = function(value) {
          return value === 1 ? 'Selected' : 'Not Selected';
        };
      } else if (question.questionType === 'radio-matrix') {
        const columns = question.columns || ['Unsatisfactory', 'Needs Improvement', 'Meets Expectations', 'Exceeds Expectations'];
        yAxisConfig.max = columns.length;
        yAxisConfig.stepSize = 1;
        yAxisConfig.title.text = 'Performance Level';
        yAxisConfig.ticks.callback = function(value) {
          if (value === 0) return 'Not Yet Completed';
          return columns[value - 1] || '';
        };
      } else if (question.questionType === 'rating') {
        yAxisConfig.max = 5;
        yAxisConfig.stepSize = 1;
        yAxisConfig.title.text = 'Rating Score';
        yAxisConfig.ticks.callback = function(value) {
          if (value === 0) return 'Not Yet Completed';
          return value;
        };
      } else {
        yAxisConfig.max = 10;
        yAxisConfig.stepSize = 1;
        yAxisConfig.title.text = 'Score';
        yAxisConfig.ticks.callback = function(value) {
          if (value === 0) return 'Not Yet Completed';
          return value;
        };
      }

      return {
        ...baseOptions,
        scales: {
          x: {
            title: {
              display: true,
              text: 'Completed Date / Due Date',
              font: {
                size: 14,
                weight: '600'
              },
              color: '#374151'
            },
            grid: {
              color: 'rgba(0, 0, 0, 0.05)',
              drawBorder: false
            },
            offset: true,
            ticks: {
              font: {
                size: 12
              },
              color: '#6b7280'
            }
          },
          y: yAxisConfig
        }
      };
    }

    if (chartType === 'bar') {
      const yAxisConfig = {
        beginAtZero: true,
        title: {
          display: true,
          font: {
            size: 14,
            weight: '600'
          },
          color: '#374151'
        },
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
          drawBorder: false
        },
        ticks: {
          font: {
            size: 12
          },
          color: '#6b7280'
        }
      };

      // Configure y-axis based on question type
      if (question.questionType === 'radio' || question.questionType === 'dropdown') {
        if (question.options && Array.isArray(question.options)) {
          yAxisConfig.max = question.options.length;
          yAxisConfig.stepSize = 1;
          yAxisConfig.title.text = 'Answer Choice';
          yAxisConfig.ticks.callback = function(value) {
            if (value === 0) return 'Not Yet Completed';
            if (value >= 1 && value <= question.options.length) {
              return question.options[value - 1];
            }
            return '';
          };
        } else {
          yAxisConfig.max = 1;
          yAxisConfig.stepSize = 1;
          yAxisConfig.title.text = 'Selection Status';
          yAxisConfig.ticks.callback = function(value) {
            return value === 1 ? 'Selected' : 'Not Selected';
          };
        }
      } else if (question.questionType === 'checkbox') {
        yAxisConfig.max = 1;
        yAxisConfig.stepSize = 1;
        yAxisConfig.title.text = 'Selection Status';
        yAxisConfig.ticks.callback = function(value) {
          return value === 1 ? 'Selected' : 'Not Selected';
        };
      } else if (question.questionType === 'radio-matrix') {
        const columns = question.columns || ['Unsatisfactory', 'Needs Improvement', 'Meets Expectations', 'Exceeds Expectations'];
        yAxisConfig.max = columns.length;
        yAxisConfig.stepSize = 1;
        yAxisConfig.title.text = 'Performance Level';
        yAxisConfig.ticks.callback = function(value) {
          if (value === 0) return 'Not Yet Completed';
          return columns[value - 1] || '';
        };
      } else if (question.questionType === 'rating') {
        yAxisConfig.max = 5;
        yAxisConfig.stepSize = 1;
        yAxisConfig.title.text = 'Rating Score';
        yAxisConfig.ticks.callback = function(value) {
          if (value === 0) return 'Not Yet Completed';
          return value;
        };
      } else {
        yAxisConfig.max = 5;
        yAxisConfig.stepSize = 1;
        yAxisConfig.title.text = 'Score';
        yAxisConfig.ticks.callback = function(value) {
          if (value === 0) return 'Not Yet Completed';
          return value;
        };
      }

      return {
        ...baseOptions,
        scales: {
          x: {
            title: {
              display: true,
              text: 'Completed Date / Due Date',
              font: {
                size: 14,
                weight: '600'
              },
              color: '#374151'
            },
            grid: {
              display: false
            },
            ticks: {
              font: {
                size: 12
              },
              color: '#6b7280'
            }
          },
          y: yAxisConfig
        }
      };
    }

    return baseOptions;
  };

  const templateQuestions = dedupeQuestions(performanceData?.template?.questions || []);

  const chartQuestions = templateQuestions.filter(
    question => question.questionType !== 'header' &&
      question.questionType !== 'paragraph' &&
      question.questionType !== 'header-paragraph'
  );

  const infoQuestions = templateQuestions.filter(
    question => question.questionType === 'header' ||
      question.questionType === 'paragraph' ||
      question.questionType === 'header-paragraph'
  );

  const hasEvaluations = (performanceData?.evaluations?.length || 0) > 0;

  const renderChart = (question, height = 350) => {
    if (!hasEvaluations) {
      return (
        <EmptyState message="No evaluations assigned for this academic year." />
      );
    }

    return (
      <div className="chart-wrapper" style={{ height: `${height}px` }}>
        {chartType === 'line' && (
          <Line
            data={prepareChartData(question)}
            options={getChartOptions('line', question)}
          />
        )}
        {chartType === 'bar' && (
          <Bar
            data={prepareBarChartData(question)}
            options={getChartOptions('bar', question)}
          />
        )}
      </div>
    );
  };

  const renderQuestionCard = (question, questionNumber, fullWidth = false) => (
    <div className="col-12" key={question.id}>
      <Card className="h-100 border p-2">
        <Card.Header className="p-2 px-3 border-0 d-flex justify-content-between align-items-start">
          <div className="d-flex align-items-center gap-2">
            <div className="survey-results-question-number">{questionNumber}</div>
            <p className="fw-semibold small mb-0">{question.questionText}</p>
          </div>
        </Card.Header>
        <Card.Body>
          {question.questionType === 'text' || question.questionType === 'textarea'
            ? renderTextResponse(question)
            : renderChart(question, fullWidth ? 450 : 350)}
        </Card.Body>
      </Card>
    </div>
  );

  const renderAllQuestionCards = () => (
    <div className="row g-4">
      {infoQuestions.map((question) => (
        <div className="col-12" key={question.id}>
          <Card className="h-100 border p-2">
            <Card.Body>{renderHeaderParagraph(question)}</Card.Body>
          </Card>
        </div>
      ))}
      {chartQuestions.map((question, index) => renderQuestionCard(question, index + 1))}
    </div>
  );

  if (loading) {
    return (
      <div className="survey-results-page min-vh-100">
        <PageTitle title="Loading..." />
        <Navbar />
        <Container className="py-5">
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
            <div className="text-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading performance data...</span>
              </div>
              <p className="mt-3 text-muted">Loading performance data...</p>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  if (!performanceData) {
    return (
      <div className="survey-results-page min-vh-100">
        <PageTitle title="Performance History" />
        <Navbar />
        <Container className="py-4">
          <Alert variant="info" className="text-center">
            <Alert.Heading>No Performance Data Found</Alert.Heading>
            <p className="mb-0">No performance data is available for the selected criteria.</p>
          </Alert>
        </Container>
      </div>
    );
  }

  const completedCount = performanceData.evaluations?.filter(e => e.hasResponse).length || 0;
  const pendingCount = performanceData.evaluations?.filter(e => !e.hasResponse).length || 0;
  const totalCount = performanceData.evaluations?.length || 0;
  const role = performanceData?.residentRole || performanceData?.role || 'resident';
  const completionRate = totalCount ? ((completedCount / totalCount) * 100).toFixed(1) : '0.0';

  return (
    <div className="survey-results-page min-vh-100">
      <PageTitle title="Performance History" description="View performance evaluation history and trends" />
      <Navbar />

      <div className="bg-white border-bottom">
        <Container className="pt-4">
          <div className="survey-results-page-header">
            <div>
              <div className="smaller text-muted text-uppercase">
                Performance History · {completedCount} of {totalCount} completed
              </div>
              <h2 className="mb-0 fw-bold">{performanceData.title}</h2>
              <p className="text-muted small mb-0">
                Evaluation for {role} {performanceData.residentName} · {selectedYear}-{selectedYear + 1}
              </p>
            </div>
            <Button variant="light" size="sm" onClick={() => navigate(-1)}>
              <i className="bi bi-arrow-left me-1" />
              Back to Dashboard
            </Button>
          </div>

            <div className="d-flex flex-wrap justify-content-between align-items-end gap-3">
              <Nav variant="pills-alt" activeKey={activeTab} onSelect={(key) => setActiveTab(key || 'overview')}>
                <Nav.Item>
                  <Nav.Link eventKey="overview">Overview</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="all">All Questions</Nav.Link>
                </Nav.Item>
                {chartQuestions.map((question, index) => (
                  <Nav.Item key={`nav-${question.id}`}>
                    <Nav.Link eventKey={`question-${question.id}`}>{`Q${index + 1}`}</Nav.Link>
                  </Nav.Item>
                ))}
                {infoQuestions.map((question, index) => (
                  <Nav.Item key={`nav-info-${question.id}`}>
                    <Nav.Link eventKey={`info-${question.id}`}>{`Info ${index + 1}`}</Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
              <div className="d-flex gap-2 flex-shrink-0 pb-1">
                <Form.Select
                  size="sm"
                  style={{ width: 'auto', minWidth: '140px' }}
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                  aria-label="Select academic year"
                >
                  {Array.from({ length: 3 }, (_, i) => {
                    const year = new Date().getFullYear() - 1 + i;
                    return (
                      <option key={year} value={year}>
                        {year}-{year + 1}
                      </option>
                    );
                  })}
                </Form.Select>
                <Form.Select
                  size="sm"
                  style={{ width: 'auto', minWidth: '120px' }}
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value)}
                  aria-label="Select chart type"
                >
                  <option value="line">Line Chart</option>
                  <option value="bar">Bar Chart</option>
                </Form.Select>
              </div>
            </div>
        </Container>
      </div>

      <Container className="py-4 pt-0">
        {activeTab === 'overview' && (
          <div className="survey-results-overview mt-4">
            <Row>
              <Col lg={3} md={6} className="mb-3">
                <MetricCard
                  icon="bi-list-check"
                  iconClass="blue"
                  label="Total Evaluations"
                  value={totalCount}
                  subtext={`${selectedYear}-${selectedYear + 1} academic year`}
                />
              </Col>
              <Col lg={3} md={6} className="mb-3">
                <MetricCard
                  icon="bi-check-circle"
                  iconClass="green"
                  label="Completed"
                  value={completedCount}
                  subtext={parseFloat(completionRate) >= 100 ? 'All submitted' : 'Submitted evaluations'}
                />
              </Col>
              <Col lg={3} md={6} className="mb-3">
                <MetricCard
                  icon="bi-clock"
                  iconClass="orange"
                  label="Pending"
                  value={pendingCount}
                  subtext="Awaiting submission"
                />
              </Col>
              <Col lg={3} md={6} className="mb-3">
                <MetricCard
                  icon="bi-layers"
                  iconClass="purple"
                  label="Questions"
                  value={chartQuestions.length}
                  subtext="Tracked in this evaluation"
                />
              </Col>
            </Row>
            <Card className="border-0 rounded-3 p-2 shadow-sm mb-4">
              <Card.Header className="border-0 text-start bg-transparent">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 pt-2">
                  <h6 className="mb-0 fw-semibold">Evaluation Details</h6>
                  {performanceData.recurrence && (
                    <Badge bg="secondary" className="rounded-pill px-3 py-2 fw-normal">
                      <i className="bi bi-arrow-repeat me-1" />
                      {performanceData.recurrence}
                    </Badge>
                  )}
                </div>
              </Card.Header>
              <Card.Body className="px-3 pt-0">
                {totalCount === 0 ? (
                  <EmptyState message="No evaluations assigned for this academic year." />
                ) : (
                  <>
                    {performanceData.evaluations.map((evaluation, index) => {
                      const completedDate = evaluation.response?.createdAt || evaluation.completedAt;
                      const isCompleted = evaluation.hasResponse || evaluation.status === 'completed';

                      return (
                        <div
                          key={evaluation.id}
                          className={index > 0 ? 'mt-4 pt-4 border-top' : ''}
                        >
                          <h6 className="fw-semibold mb-3">Evaluation {index + 1}</h6>
                          <Row className="g-3">
                            <Col md={4}>
                              <DetailTile
                                icon="bi-calendar-plus"
                                iconBg="bg-primary bg-opacity-10"
                                iconColor="text-primary"
                                label="Assigned"
                                value={formatEvalDate(evaluation.createdAt)}
                              />
                            </Col>
                            <Col md={4}>
                              <DetailTile
                                icon="bi-calendar-event"
                                iconBg="bg-warning bg-opacity-10"
                                iconColor="text-warning"
                                label="Due Date"
                                value={formatEvalDate(evaluation.dueDate)}
                              />
                            </Col>
                            <Col md={4}>
                              <DetailTile
                                icon="bi-calendar3"
                                iconBg="bg-secondary bg-opacity-10"
                                iconColor="text-secondary"
                                label="Academic Year"
                                value={`${selectedYear}-${selectedYear + 1}`}
                              />
                            </Col>
                            <Col md={4}>
                              <DetailTile
                                icon="bi-calendar-check"
                                iconBg="bg-success bg-opacity-10"
                                iconColor="text-success"
                                label="Completed"
                                value={isCompleted ? formatEvalDate(completedDate) : '—'}
                              />
                            </Col>
                            <Col md={4}>
                              <DetailTile
                                icon="bi-person-fill"
                                iconBg="bg-info bg-opacity-10"
                                iconColor="text-info"
                                label="Completed By"
                                value={isCompleted ? getEvaluatorName(evaluation.evaluator) : '—'}
                              />
                            </Col>
                            <Col md={4}>
                              <DetailTile
                                icon={isCompleted ? 'bi-check-circle-fill' : 'bi-clock-history'}
                                iconBg={isCompleted ? 'bg-success bg-opacity-10' : 'bg-warning bg-opacity-10'}
                                iconColor={isCompleted ? 'text-success' : 'text-warning'}
                                label="Status"
                                value={isCompleted ? 'Completed' : 'Pending'}
                              />
                            </Col>
                          </Row>
                        </div>
                      );
                    })}
                  </>
                )}
              </Card.Body>
            </Card>
            <Card className="mb-4 bg-dark border-0">
              <Card.Body className="p-4 d-flex flex-row justify-content-between">
                <div>
                  <Card.Title className="text-uppercase smaller text-white mb-0">
                    Overall Assessment
                  </Card.Title>
                  <Card.Text className="text-white mb-0">
                    {getAssessmentText(completedCount, totalCount)}
                  </Card.Text>
                </div>
                <Button variant="light-solid" onClick={() => setActiveTab('all')}>
                  View all questions →
                </Button>
              </Card.Body>
            </Card>


          </div>
        )}

        <div className="mt-4">
          {activeTab === 'all' && renderAllQuestionCards()}

          {chartQuestions.map((question, index) => (
            activeTab === `question-${question.id}` && (
              <div key={question.id} className="row g-4">
                {renderQuestionCard(question, index + 1, true)}
              </div>
            )
          ))}

          {infoQuestions.map((question) => (
            activeTab === `info-${question.id}` && (
              <Card key={question.id} className="border p-2">
                <Card.Body>{renderHeaderParagraph(question)}</Card.Body>
              </Card>
            )
          ))}
        </div>
      </Container>
    </div>
  );
};

export default PerformanceHistory; 
